import { useEffect, useMemo } from 'react';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys, queryValues } from '~/routes/query-params';
import { PosthogTrackingSource } from '~/services/tracking/posthog/constants';
import { usePosthogEvents } from '~/services/tracking/posthog/use-posthog-events/usePosthogEvents';

export enum SubTabsType {
  Preview = 'preview',
  Recap = 'recap',
}

const trackedDrawers = new Set<string>();

export const useEarningsInsightDrawer = () => {
  const { deleteQueryParam, setQueryParams, params } = useQueryParams();
  const { trackEarningsInsightDrawerOpen } = usePosthogEvents();
  const isDrawerOpen =
    params[queryKeys.activeDrawer] === queryValues.earningsInsightDrawer;
  const activeEntityTag = params[queryKeys.activeEarningsEntity];
  const earningsId = params[queryKeys.earningsId];

  const drawerId = useMemo(() => {
    return activeEntityTag && earningsId
      ? `${activeEntityTag}-${earningsId}`
      : null;
  }, [activeEntityTag, earningsId]);

  const subtabs = [SubTabsType.Preview, SubTabsType.Recap];
  const foundTabIndex = subtabs.findIndex(
    tab => tab === params[queryKeys.activeDrawerTab]
  );
  const activeTabIndex = foundTabIndex !== -1 ? foundTabIndex : 0;
  const activeTab = subtabs[activeTabIndex];

  const openDrawer = ({
    entityTag,
    earningsId,
    subtab,
    source,
  }: {
    entityTag: string;
    earningsId: string;
    subtab?: SubTabsType;
    source: PosthogTrackingSource;
  }) => {
    const drawerType = subtab ?? SubTabsType.Preview;
    trackEarningsInsightDrawerOpen(source, drawerType);
    setQueryParams({
      [queryKeys.activeDrawer]: queryValues.earningsInsightDrawer,
      [queryKeys.activeDrawerTab]: drawerType,
      [queryKeys.activeEarningsEntity]: entityTag,
      [queryKeys.earningsId]: earningsId,
    });
  };

  const closeDrawer = () =>
    deleteQueryParam([
      queryKeys.activeDrawer,
      queryKeys.activeDrawerTab,
      queryKeys.activeEarningsEntity,
      queryKeys.earningsId,
      queryKeys.utmSource,
      queryKeys.utmCampaign,
    ]);

  const switchTab = (index: number) => {
    setQueryParams({ [queryKeys.activeDrawerTab]: subtabs[index] });
  };

  const utmSource = params[queryKeys.utmSource];

  useEffect(() => {
    if (!isDrawerOpen || !drawerId || !utmSource) {
      if (!isDrawerOpen && drawerId && trackedDrawers.has(drawerId)) {
        trackedDrawers.delete(drawerId);
      }

      return;
    }

    if (trackedDrawers.has(drawerId)) {
      return;
    }

    trackedDrawers.add(drawerId);

    trackEarningsInsightDrawerOpen(
      utmSource as PosthogTrackingSource,
      activeTab
    );
  }, [isDrawerOpen, drawerId]);

  return {
    closeDrawer,
    openDrawer,
    isDrawerOpen,
    activeEntityTag,
    switchTab,
    activeTabIndex,
    activeTab,
    earningsId,
  };
};
